import type { MentionDto } from '@/entities/mentions'
import type { ListboxValueChangeDetails } from '@ark-ui/react/listbox'
import type {
  MentionSuggestionsCommandReturn,
  MentionSuggestionsHandle,
} from './mentions.config'

import { useMentions } from '@/entities/mentions'
import { rundownAtom } from '@/store/rundown.store'
import { useAtomValue } from 'jotai'

import { cn } from '@/utils'
import { useCallback, useImperativeHandle, useMemo, useState } from 'react'

import { faAdd, faAt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  Listbox,
  createListCollection,
  useListbox,
} from '@ark-ui/react/listbox'

import {
  TiptapSuggestionListItem,
  TiptapSuggestionsLoading,
  TiptapSuggestionsWidget,
} from '@/entities/tiptap-suggestions'

import { getMentionColors } from './mentions.utils'
import type { SuggestionProps } from '@tiptap/suggestion'
import { modalActions } from '@/features/rundown/modals/modal.store'

type MentionItem = { kind: 'mention' } & MentionDto

type ActionItem = {
  kind: 'action'
  id: 'action.manage' | 'action.create'
  label: string
}

type ListItemType = MentionItem | ActionItem

type MentionSuggestionsListProps = SuggestionProps<
  MentionDto,
  MentionSuggestionsCommandReturn
> & {
  ref: React.Ref<MentionSuggestionsHandle>
}

export function MentionSuggestionsList({
  query,
  command,
  ref,
}: MentionSuggestionsListProps) {
  const rundown = useAtomValue(rundownAtom)

  const { data: mentions, isLoading } = useMentions({
    params: {
      rundownId: rundown!.id,
    },
    options: {
      enabled: !!rundown?.id,
    },
  })

  const filteredMentions = useMemo(
    () =>
      mentions?.filter(({ name }) =>
        [name]
          .map((n) => n.trim().toLowerCase())
          .join('')
          .includes(query.toLowerCase()),
      ),
    [mentions, query],
  )

  const items: ListItemType[] = useMemo(() => {
    const hasMentions = (filteredMentions?.length ?? 0) > 0
    if (hasMentions) {
      // Show mentions and add a special Manage action at the end
      return [
        ...(filteredMentions ?? []).map((m) => ({
          ...m,
          kind: 'mention' as const,
        })),
        { kind: 'action', id: 'action.manage', label: 'Manage @Mentions' },
      ]
    }

    // No mentions: only the Create action
    return [{ kind: 'action', id: 'action.create', label: 'Create @Mentions' }]
  }, [filteredMentions])

  const collection = createListCollection<ListItemType>({
    items,
    itemToValue: (item) => item.id,
    itemToString: (item) => item.id,
  })

  const handleValueChange = useCallback(
    ({ value: newIds }: ListboxValueChangeDetails<ListItemType>) => {
      const selected = collection.find(newIds[0])

      if (!selected) return

      if (selected.kind === 'mention') {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { kind, ...mention } = selected

        command({ mention })
      } else if (selected.kind === 'action') {
        // Open modal for manage/create
        modalActions.open('MENTIONS', {})
      }
    },
    [collection, command],
  )

  const [highlighted, setHighlighted] = useState<string | null>(
    () => (items[0]?.id as string | undefined) ?? null,
  )
  const listbox = useListbox({
    collection: collection,
    onValueChange: handleValueChange,
    highlightedValue: highlighted,
    onHighlightChange: ({ highlightedValue }) =>
      setHighlighted(highlightedValue),
    selectionMode: 'single',
    loopFocus: true,
    orientation: 'vertical',
    deselectable: true,
  })

  const handleUp = useCallback(() => {
    const { firstValue, lastValue, getPreviousValue } = listbox.collection

    setHighlighted((prev) => {
      if (prev === firstValue) return lastValue

      if (prev) {
        return getPreviousValue(prev) ?? null
      }

      return firstValue
    })
  }, [listbox.collection])

  const handleDown = useCallback(() => {
    const { firstValue, lastValue, getNextValue } = listbox.collection

    setHighlighted((prev) => {
      if (prev === lastValue) return firstValue

      if (prev) {
        return getNextValue(prev) ?? null
      }

      return firstValue
    })
  }, [listbox.collection])

  const handleEnter = useCallback(() => {
    if (!highlighted) return

    const selected = collection.find(highlighted)
    if (!selected) return

    if (selected.kind === 'mention') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { kind, ...mention } = selected

      command({ mention })
    } else if (selected.kind === 'action') {
      modalActions.open('MENTIONS', {})
    }
  }, [collection, highlighted, command])

  useImperativeHandle(ref, () => ({
    onKeyDown({ event }) {
      switch (event.key) {
        case 'ArrowUp': {
          handleUp()
          return true
        }
        case 'ArrowDown': {
          handleDown()
          return true
        }
        case 'Enter': {
          handleEnter()
          return true
        }
      }

      // Don't handle unknown keys
      return false
    },
  }))

  if (isLoading) {
    return <TiptapSuggestionsLoading text="Loading mentions..." />
  }

  return (
    <>
      <TiptapSuggestionsWidget>
        <Listbox.RootProvider value={listbox}>
          <Listbox.Content
            {...listbox.getContentProps()}
            className="flex flex-col gap-1 divide-y divide-gray-600"
          >
            {collection.items.map((item, index) => {
              const isHighlighted = highlighted === item.id
              const itemProps = {
                item,
                highlightOnHover: true,
              }

              if (item.kind === 'mention') {
                const { id, color, name } = item

                const {
                  color: textColor,
                  backgroundColor,
                  iconBackgroundColor,
                } = getMentionColors(color)

                return (
                  <TiptapSuggestionListItem
                    {...listbox.getItemProps(itemProps)}
                    key={id}
                    item={item}
                    className={cn([
                      'mx-0.75 rounded-sm border-0',
                      index === 0 ? 'mt-0.75' : '',
                      isHighlighted ? 'ring-3' : '',
                    ])}
                  >
                    <Listbox.ItemText
                      {...listbox.getItemTextProps(itemProps)}
                      className="text-sm w-min whitespace-pre flex rounded-sm"
                      style={{
                        backgroundColor,
                        color: textColor,
                      }}
                    >
                      <span
                        className="w-auto h-auto flex items-center justify-center text-xs rounded-l px-1 text-center"
                        style={{
                          backgroundColor: iconBackgroundColor,
                          color: textColor,
                        }}
                      >
                        <FontAwesomeIcon icon={faAt} size="xs" />
                      </span>
                      <span
                        className="px-1 rounded-r border border-l-0"
                        style={{
                          borderColor: color,
                          color: textColor,
                        }}
                      >
                        {name}
                      </span>
                    </Listbox.ItemText>
                  </TiptapSuggestionListItem>
                )
              }

              return (
                <TiptapSuggestionListItem
                  {...listbox.getItemProps(itemProps)}
                  key={item.id}
                  item={item}
                  className={cn(
                    'bg-gray-700',
                    isHighlighted && 'ring-inset ring-3',
                  )}
                >
                  <Listbox.ItemText
                    {...listbox.getItemTextProps(itemProps)}
                    className="text-sm"
                  >
                    {item.label}
                  </Listbox.ItemText>
                </TiptapSuggestionListItem>
              )
            })}
          </Listbox.Content>
          <Listbox.Empty
            className={cn(
              'px-1.5 py-px cursor-pointer',
              highlighted === 'action.create' && 'hover:bg-gray-800',
            )}
            onClick={() => modalActions.open('MENTIONS', {})}
          >
            <FontAwesomeIcon icon={faAdd} className="mr-1" />
            Create @Mentions
          </Listbox.Empty>
        </Listbox.RootProvider>
      </TiptapSuggestionsWidget>
    </>
  )
}
